import { useCallback, useRef, useState } from 'react'

/**
 * Options for the tree data processor
 * @template TInput Type of input items to process
 * @template TResult Type of processed result
 */

/** Constant large dataset threshold for content item nodes to trigger batch processing */
export const LARGE_CONTENT_ITEM_NODES_THRESHOLD = 500

interface ProcessorOptions<TInput, TResult> {
    /** Function that processes a batch of items */
    processorFn: (items: TInput[]) => TResult[]
    /** Callback when processing completes */
    onComplete: (results: TResult[]) => void
    /** Callback when an error occurs (optional) */
    onError?: (error: Error) => void
    /** Size threshold to switch between sync/batch processing (default: 500) */
    threshold?: number
    /** Size of batches for batch processing (default: 50) */
    batchSize?: number
}

/** State for tracking batch processing */
interface BatchProcessingState<TResult> {
    /** Whether batch processing is currently in progress */
    isProcessing: boolean
    /** Number of items processed so far */
    processedCount: number
    /** Results of processed items */
    results: TResult[]
    /** Cleanup function for ongoing processing */
    cleanup: (() => void) | null
}

/**
 * Custom hook for processing large datasets in a non-blocking way.
 * Uses batched processing for large datasets to prevent UI freezing.
 *
 * @template TInput Type of input items to process
 * @template TResult Type of processed result
 */
export const useTreeDataProcessor = <TInput, TResult>({
    processorFn,
    onComplete,
    onError,
    threshold = LARGE_CONTENT_ITEM_NODES_THRESHOLD,
    batchSize = 20,
}: ProcessorOptions<TInput, TResult>) => {
    const [isLoading, setIsLoading] = useState(false)

    // to track all batch processing state
    const batchStateRef = useRef<BatchProcessingState<TResult>>({
        isProcessing: false,
        processedCount: 0,
        results: [],
        cleanup: null,
    })

    const handleError = useCallback(
        (error: Error, context: string) => {
            console.error(`Error in ${context}:`, error)
            onError?.(error)
        },
        [onError]
    )

    const cleanup = useCallback(() => {
        const state = batchStateRef.current
        if (state.cleanup) {
            state.cleanup()
            state.cleanup = null
        }
        state.isProcessing = false
        state.processedCount = 0
        state.results = []
        setIsLoading(false)
    }, [])

    // for small datasets
    const processSynchronously = useCallback(
        (items: TInput[]): void => {
            if (items.length === 0) {
                onComplete([])
                return
            }

            try {
                setIsLoading(true)
                const results = processorFn(items)
                onComplete(results)
            } catch (error) {
                handleError(error as Error, 'synchronous processing')
            } finally {
                setIsLoading(false)
            }
        },
        [processorFn, onComplete, handleError]
    )

    // schedule next batch using requestIdleCallback or setTimeout
    const scheduleNextBatch = useCallback((callback: () => void):
        | number
        | NodeJS.Timeout => {
        // use requestIdleCallback if available for better performance
        if ('requestIdleCallback' in window) {
            return (window as any).requestIdleCallback(callback, {
                timeout: 16,
            })
        }

        return setTimeout(callback, 0)
    }, [])

    const cancelScheduledBatch = useCallback((id: number | NodeJS.Timeout) => {
        if ('cancelIdleCallback' in window && typeof id === 'number') {
            ;(window as any).cancelIdleCallback(id)
        } else {
            clearTimeout(id as NodeJS.Timeout)
        }
    }, [])

    // process items in batches (for large datasets)
    const processBatched = useCallback(
        (items: TInput[]): (() => void) => {
            if (items.length === 0) {
                onComplete([])
                return () => {}
            }

            const state = batchStateRef.current
            if (state.isProcessing) {
                return () => {}
            }

            cleanup()

            // initialize batch processing state
            state.isProcessing = true
            state.processedCount = 0
            state.results = []
            setIsLoading(true)

            let scheduledId: number | NodeJS.Timeout | null = null

            const processSingleBatch = () => {
                const state = batchStateRef.current

                if (!state.isProcessing) {
                    return
                }

                const startIndex = state.processedCount
                const endIndex = Math.min(startIndex + batchSize, items.length)
                const batch = items.slice(startIndex, endIndex)

                try {
                    const batchResults = processorFn(batch)
                    state.results.push(...batchResults)
                    state.processedCount = endIndex

                    // do we have more batches to process?
                    if (state.processedCount < items.length) {
                        scheduledId = scheduleNextBatch(processSingleBatch)
                    } else {
                        const finalResults = [...state.results]
                        cleanup()
                        // notify completion
                        onComplete(finalResults)
                    }
                } catch (error) {
                    cleanup()
                    handleError(error as Error, 'batch processing')
                }
            }

            state.cleanup = () => {
                if (scheduledId !== null) {
                    cancelScheduledBatch(scheduledId)
                    scheduledId = null
                }
            }

            // start processing after a short delay to ensure UI updates
            scheduledId = setTimeout(processSingleBatch, 10)

            return () => cleanup()
        },
        [
            processorFn,
            onComplete,
            handleError,
            batchSize,
            cleanup,
            scheduleNextBatch,
            cancelScheduledBatch,
        ]
    )

    // main processing function that decides between sync and batch processing
    const mapTreeData = useCallback(
        (items: TInput[]): (() => void) => {
            cleanup()

            if (items.length === 0) {
                onComplete([])
                return () => {}
            }

            if (items.length <= threshold) {
                processSynchronously(items)
                return () => {}
            } else {
                return processBatched(items)
            }
        },
        [processSynchronously, processBatched, threshold, cleanup, onComplete]
    )

    return {
        isLoading,
        mapTreeData,
    }
}
